import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useClientPagination } from '../../lib/usePagination.js';
import {
  PageActionStrip,
  ErrorState,
  StatusChip,
  HealthIndicator,
  GanttPanel,
  InspectorCard,
  Sparkline,
  FormField,
  TextInput,
  NumberInput,
  DateInput,
  Select,
  Textarea,
  PageViewerControls,
  toast,
  activityRail,
  useInspector,
  KpiHeroBand,
  deriveKpiTone,
  InlineScoreCell,
  bandTone,
  KpiBandSkeleton,
  TableSkeleton,
  BrandedEmptyState,
} from '../../components/index.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { PageHeader } from '../../app/PageHeader.jsx';
import { NewEpicDrawer } from '../delivery/NewEpicDrawer.jsx';
import './roadmap.css';
import './roadmap.redesign.css';

const VIEWS = ['Gantt', 'List', 'Board'];
const SCALES = ['D', 'W', 'M', 'Q'];

const STATUS_OPTIONS = [
  { value: 'not-started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'at-risk', label: 'At risk' },
  { value: 'on_track', label: 'On track' },
  { value: 'complete', label: 'Complete' },
  { value: 'blocked', label: 'Blocked' },
];

function toneFor(status) {
  const s = String(status || '').toLowerCase();
  if (/done|complete|stable|on_?track/.test(s)) return 'sage';
  if (/risk|amber|watch/.test(s)) return 'amber';
  if (/red|critical|block/.test(s)) return 'brick';
  return 'blue';
}

function parseDateOnly(value) {
  if (!value) return Number.NaN;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? Number.NaN : ts;
}

function toIsoDate(ts) {
  return new Date(ts).toISOString().slice(0, 10);
}

function updateEnvelopeRow(prev, updated) {
  if (!prev?.data?.data || !updated?.id) return prev;
  return {
    ...prev,
    data: {
      ...prev.data,
      data: prev.data.data.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)),
    },
  };
}

function mergeEnvelopeRows(prev, updates = []) {
  if (!prev?.data?.data || !updates.length) return prev;
  const updateMap = new Map(updates.filter((row) => row?.id).map((row) => [row.id, row]));
  return {
    ...prev,
    data: {
      ...prev.data,
      data: prev.data.data.map((row) =>
        updateMap.has(row.id) ? { ...row, ...updateMap.get(row.id) } : row,
      ),
    },
  };
}

function sortWorkItemsByDependencies(workItems, dependencies) {
  const ids = workItems.map((item) => item.id);
  const idSet = new Set(ids);
  const indegree = new Map(ids.map((id) => [id, 0]));
  const adjacency = new Map(ids.map((id) => [id, []]));
  for (const dependency of dependencies) {
    if (!idSet.has(dependency.predecessorId) || !idSet.has(dependency.successorId)) continue;
    adjacency.get(dependency.predecessorId).push(dependency.successorId);
    indegree.set(dependency.successorId, (indegree.get(dependency.successorId) || 0) + 1);
  }

  const queue = ids.filter((id) => indegree.get(id) === 0).sort();
  const orderedIds = [];
  while (queue.length) {
    const id = queue.shift();
    orderedIds.push(id);
    for (const nextId of adjacency.get(id) || []) {
      indegree.set(nextId, (indegree.get(nextId) || 0) - 1);
      if (indegree.get(nextId) === 0) {
        queue.push(nextId);
        queue.sort();
      }
    }
  }

  const trailingIds = ids.filter((id) => !orderedIds.includes(id));
  const orderedIdSet = new Set([...orderedIds, ...trailingIds]);
  return [
    ...orderedIds.map((id) => workItems.find((item) => item.id === id)),
    ...workItems.filter((item) => !orderedIdSet.has(item.id)),
  ].filter(Boolean);
}

function deriveRoadmapTaskBars({ projects, workItems, roadmapItems, dependencies, focusId }) {
  if (!focusId) return [];
  const focusedProject = projects.find((project) => project.id === focusId);
  const portfolioId = focusedProject?.portfolioId || 'PF-UNGROUPED';
  const workItemsById = new Map(workItems.map((item) => [item.id, item]));
  const relevantRoadmapItems = roadmapItems.filter(
    (item) =>
      item.scopeType === 'project' && item.scopeId === focusId && item.linkedWorkItemIds?.length,
  );

  return relevantRoadmapItems.flatMap((roadmapItem) => {
    const roadmapStartTs = parseDateOnly(roadmapItem.startDate);
    const roadmapEndTs = parseDateOnly(roadmapItem.endDate);
    if (
      Number.isNaN(roadmapStartTs) ||
      Number.isNaN(roadmapEndTs) ||
      roadmapEndTs < roadmapStartTs
    ) {
      return [];
    }

    const linkedItems = roadmapItem.linkedWorkItemIds
      .map((id) => workItemsById.get(id))
      .filter(Boolean);
    if (!linkedItems.length) return [];

    const linkedItemIds = new Set(linkedItems.map((item) => item.id));
    const linkedDependencies = dependencies.filter(
      (dependency) =>
        linkedItemIds.has(dependency.predecessorId) && linkedItemIds.has(dependency.successorId),
    );
    const orderedItems = sortWorkItemsByDependencies(linkedItems, linkedDependencies);
    const totalDays = Math.max(
      1,
      Math.round((roadmapEndTs - roadmapStartTs) / (24 * 60 * 60 * 1000)) + 1,
    );
    const baselineStartTs = parseDateOnly(roadmapItem.baselineStart || roadmapItem.startDate);
    const baselineEndTs = parseDateOnly(roadmapItem.baselineEnd || roadmapItem.endDate);
    const baselineDays =
      Number.isNaN(baselineStartTs) || Number.isNaN(baselineEndTs)
        ? totalDays
        : Math.max(1, Math.round((baselineEndTs - baselineStartTs) / (24 * 60 * 60 * 1000)) + 1);

    return orderedItems.map((workItem, index) => {
      const startOffset = Math.floor((index * totalDays) / orderedItems.length);
      const nextOffset = Math.floor(((index + 1) * totalDays) / orderedItems.length);
      const endOffset = Math.max(startOffset, nextOffset - 1);
      const baselineStartOffset = Math.floor((index * baselineDays) / orderedItems.length);
      const baselineNextOffset = Math.floor(((index + 1) * baselineDays) / orderedItems.length);
      const baselineEndOffset = Math.max(baselineStartOffset, baselineNextOffset - 1);
      // TT-07a / AC-CMD-3.1 — a work item with authored dates renders on those
      // dates as a solid bar. Otherwise the bar geometry is synthesized
      // (evenly spaced inside the roadmap-item window) and MUST carry the
      // "estimated" treatment so invented geometry is never mistaken for data.
      const authoredStartTs = parseDateOnly(workItem.startDate);
      const authoredEndTs = parseDateOnly(workItem.endDate);
      const hasAuthoredDates =
        !Number.isNaN(authoredStartTs) &&
        !Number.isNaN(authoredEndTs) &&
        authoredEndTs >= authoredStartTs;
      const tone = toneFor(workItem.status);
      return {
        id: workItem.id,
        name: workItem.title || workItem.name,
        startDate: hasAuthoredDates
          ? toIsoDate(authoredStartTs)
          : toIsoDate(roadmapStartTs + startOffset * 24 * 60 * 60 * 1000),
        endDate: hasAuthoredDates
          ? toIsoDate(authoredEndTs)
          : toIsoDate(roadmapStartTs + endOffset * 24 * 60 * 60 * 1000),
        baselineStartDate: hasAuthoredDates
          ? workItem.baselineStart || undefined
          : toIsoDate(
              (Number.isNaN(baselineStartTs) ? roadmapStartTs : baselineStartTs) +
                baselineStartOffset * 24 * 60 * 60 * 1000,
            ),
        baselineEndDate: hasAuthoredDates
          ? workItem.baselineEnd || undefined
          : toIsoDate(
              (Number.isNaN(baselineStartTs) ? roadmapStartTs : baselineStartTs) +
                baselineEndOffset * 24 * 60 * 60 * 1000,
            ),
        // TT-07b — progress renders only when the work item reports it.
        progress: workItem.percentComplete != null ? Number(workItem.percentComplete) / 100 : null,
        // The estimated class rides the tone string so the shared GanttPanel
        // (out of scope for this change) emits `gantt-bar tone-<t> is-estimated`.
        tone: hasAuthoredDates ? tone : `${tone} is-estimated`,
        datesEstimated: !hasAuthoredDates,
        criticalPath: Boolean(workItem.criticalPathFlag),
        milestones: [],
        __raw: {
          ...workItem,
          projectId: workItem.projectId || focusId,
          portfolioId,
        },
        __entityType: 'work_item',
      };
    });
  });
}

function buildLanes(projects, epics, milestones, focusId, roadmapTaskBars = []) {
  const portfolios = {};
  for (const p of projects) {
    const key = p.portfolioId || 'PF-UNGROUPED';
    if (!portfolios[key]) portfolios[key] = { id: key, label: key.replace('PF-', ''), items: [] };
    // TT-07a / AC-CMD-3.1 — when a project has no authored dates the bar is
    // placed on a default window purely so it stays visible, but it renders
    // with the distinct "estimated" treatment (never a solid authored-looking bar).
    const authoredStart = p.startDate || p.plannedStartDate || p.lifecycleDates?.start || null;
    const authoredEnd = p.endDate || p.plannedEndDate || p.lifecycleDates?.end || null;
    const datesEstimated = !authoredStart || !authoredEnd;
    const start = authoredStart || '2026-01-01';
    const end = authoredEnd || '2026-12-31';
    const tone = toneFor(p.status);
    portfolios[key].items.push({
      id: p.id,
      name: p.name,
      startDate: start,
      endDate: end,
      baselineStartDate: p.plannedStartDate || p.baselineStart || start,
      baselineEndDate: p.plannedEndDate || p.baselineEnd || end,
      // TT-07b — no invented 60-health progress fill; absent scores render no fill.
      progress: p.healthScores?.overall != null ? Number(p.healthScores.overall) / 100 : null,
      richTooltip: p.richTooltip || null,
      tone: datesEstimated ? `${tone} is-estimated` : tone,
      datesEstimated,
      criticalPath: Boolean(p.criticalPath),
      milestones: milestones
        .filter((m) => m.projectId === p.id || m.scopeId === p.id)
        .slice(0, 3)
        .map((m) => ({
          id: m.id,
          date: m.targetDate || m.dueDate || start,
          label: m.name || m.title,
          tone: toneFor(m.status),
          richTooltip: m.richTooltip || null,
          __raw: m,
        })),
      __raw: p,
      __entityType: 'project',
    });
  }
  // Append epics into the matching project's portfolio lane.
  for (const e of epics) {
    if (!e.startDate || !e.endDate) continue;
    const project = projects.find((p) => p.id === e.projectId);
    const key = e.portfolioId || project?.portfolioId || 'PF-UNGROUPED';
    if (!portfolios[key]) portfolios[key] = { id: key, label: key.replace('PF-', ''), items: [] };
    portfolios[key].items.push({
      id: e.id,
      name: `◆ ${e.title || e.name}`,
      startDate: e.startDate,
      endDate: e.endDate,
      baselineStartDate: e.baselineStart || e.startDate,
      baselineEndDate: e.baselineEnd || e.endDate,
      // TT-07b — epics without a reported percentComplete render no fake fill.
      progress: e.percentComplete != null ? Number(e.percentComplete) / 100 : null,
      tone: toneFor(e.status),
      datesEstimated: false,
      criticalPath: Boolean(e.criticalPathFlag),
      milestones: [],
      __raw: e,
      __entityType: 'work_item',
    });
  }
  for (const task of roadmapTaskBars) {
    const project = projects.find((candidate) => candidate.id === task.__raw?.projectId);
    const key = task.__raw?.portfolioId || project?.portfolioId || 'PF-UNGROUPED';
    if (!portfolios[key]) portfolios[key] = { id: key, label: key.replace('PF-', ''), items: [] };
    portfolios[key].items.push(task);
  }
  return Object.values(portfolios).sort((a, b) => {
    if (focusId) {
      const aHas = a.items.some((it) => it.id === focusId) ? -1 : 0;
      const bHas = b.items.some((it) => it.id === focusId) ? -1 : 0;
      return aHas - bHas;
    }
    return a.label.localeCompare(b.label);
  });
}

const EMPTY_EDIT = {
  name: '',
  status: '',
  ownerUserId: '',
  startDate: '',
  endDate: '',
  schedule: '',
  cost: '',
  risk: '',
  missionImpact: '',
  budgetUsdM: '',
  notes: '',
};

function projectToEditState(p) {
  if (!p) return EMPTY_EDIT;
  return {
    name: p.name || '',
    status: p.status || '',
    ownerUserId: p.projectManagerUserId || p.ownerUserId || '',
    startDate: (p.startDate || p.lifecycleDates?.start || '').slice(0, 10),
    endDate: (p.endDate || p.lifecycleDates?.end || '').slice(0, 10),
    schedule: p.healthScores?.schedule ?? '',
    cost: p.healthScores?.cost ?? '',
    risk: p.healthScores?.risk ?? '',
    missionImpact: p.healthScores?.missionImpact ?? '',
    budgetUsdM: p.budgetUsdM ?? p.plannedBudgetUsdM ?? '',
    notes: '',
  };
}

function formatStatusBucketLabel(status) {
  const normalized = String(status || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  const compact = normalized.toLowerCase().replace(/\s+/g, '');
  const aliases = {
    inprogress: 'in progress',
    notstarted: 'not started',
    ontrack: 'on track',
    atrisk: 'at risk',
    todo: 'to do',
  };
  return aliases[compact] || normalized.toLowerCase();
}

// DATA-TRUST: the inspector trend was a hardcoded ramp ([40,48,52,…]). Build the
// series from the focused item's REAL health-dimension scores instead, so the
// line is a faithful cross-dimension health profile of the selected entity.
// Returns null when the item carries no scores so the viz can be omitted.
const HEALTH_DIMENSIONS = [
  { key: 'schedule', label: 'Schedule' },
  { key: 'cost', label: 'Cost' },
  { key: 'risk', label: 'Risk' },
  { key: 'missionImpact', label: 'Mission' },
  { key: 'overall', label: 'Overall' },
];

function buildInspectorHealthSeries(target) {
  const scores = target?.healthScores || {};
  const points = HEALTH_DIMENSIONS.map(({ key, label }) => ({
    label,
    value: scores[key],
  })).filter((p) => p.value != null && !Number.isNaN(Number(p.value)));
  if (points.length < 2) return null;
  const values = points.map((p) => Number(p.value));
  return {
    values,
    points,
    labels: points.map((p) => p.label),
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

// Always-on aggregate roadmap metrics (shown when no ?focus= is selected).
// Every value is derived from data already fetched — no fabricated numbers.
function buildAggregateKpis({ projects, projectTotal, milestones, workItems, lanes }) {
  const projectCount = Number(projectTotal ?? projects.length);
  const portfolioCount = lanes.length;
  const overallScores = projects
    .map((p) => Number(p.healthScores?.overall))
    .filter((n) => !Number.isNaN(n));
  const avgHealth = overallScores.length
    ? Math.round(overallScores.reduce((a, n) => a + n, 0) / overallScores.length)
    : null;
  const healthSpark = overallScores.slice(0, 16);
  const atRiskCount = projects.filter((p) => {
    const s = String(p.status || '').toLowerCase();
    return /risk|amber|watch|red|critical|block/.test(s);
  }).length;
  const criticalMilestones = milestones.filter((m) => {
    const s = String(m.status || '').toLowerCase();
    return /risk|amber|red|critical|block|slip/.test(s);
  }).length;
  const blockedWork = workItems.filter(
    (w) => String(w.status || '').toLowerCase() === 'blocked',
  ).length;

  const items = [
    {
      id: 'roadmap-initiatives',
      label: 'Active initiatives',
      value: projectCount,
      footer: `Across ${portfolioCount} portfolio${portfolioCount === 1 ? '' : 's'}`,
      status: 'blue',
      statusLabel: 'Portfolio',
    },
    {
      id: 'roadmap-health',
      label: 'Avg overall health',
      value: avgHealth == null ? '—' : avgHealth,
      unit: avgHealth == null ? undefined : '/100',
      sparkline: healthSpark.length > 1 ? healthSpark : undefined,
      sparklineTone: avgHealth == null ? 'slate' : deriveKpiTone({ value: avgHealth, target: 80 }),
      status: avgHealth == null ? 'slate' : deriveKpiTone({ value: avgHealth, target: 80 }),
      statusLabel: avgHealth == null ? 'No score' : 'Health',
      footer: `${overallScores.length} scored initiatives`,
    },
    {
      id: 'roadmap-at-risk',
      label: 'At-risk initiatives',
      value: atRiskCount,
      status: atRiskCount > 0 ? 'amber' : 'sage',
      statusLabel: atRiskCount > 0 ? 'Watch' : 'Clear',
      footer:
        projectCount > 0
          ? `${Math.round((atRiskCount / projectCount) * 100)}% of the portfolio`
          : 'No initiatives loaded',
    },
    {
      id: 'roadmap-milestones',
      label: 'Milestones in jeopardy',
      value: criticalMilestones,
      status: criticalMilestones > 0 ? 'amber' : 'sage',
      statusLabel: criticalMilestones > 0 ? 'Slip risk' : 'On plan',
      footer: `${blockedWork} blocked work item${blockedWork === 1 ? '' : 's'} in scope`,
    },
  ];
  return items;
}

function buildFocusMetrics(project, deliverySummary) {
  if (!project || !deliverySummary) return [];
  return [
    {
      label: 'Baseline variance',
      value: `${deliverySummary.baselineVarianceDays || 0} days`,
      tone: deliverySummary.baselineVarianceDays > 0 ? 'amber' : 'sage',
      detail:
        deliverySummary.baselineVarianceDays > 0
          ? 'Current schedule is running beyond the baseline plan.'
          : 'Current schedule is holding to the baseline plan.',
    },
    {
      label: 'Critical path items',
      value: `${deliverySummary.criticalPathItemCount || 0}`,
      tone: deliverySummary.criticalPathItemCount > 0 ? 'amber' : 'sage',
      detail: `${deliverySummary.totalCriticalPathDays || 0} total critical-path days.`,
    },
    {
      label: 'Blocked work',
      value: `${deliverySummary.blockedWorkCount || 0}`,
      tone: deliverySummary.blockedWorkCount > 0 ? 'brick' : 'sage',
      detail: `${deliverySummary.dependencyCount || 0} active dependency links in scope.`,
    },
    {
      label: 'Milestone slip',
      value: `${deliverySummary.milestoneSlipCount || 0}`,
      tone: deliverySummary.milestoneSlipCount > 0 ? 'amber' : 'sage',
      detail: `${deliverySummary.sprintSummary?.activeSprintCount || 0} active sprint windows.`,
    },
  ];
}

export function StrategicRoadmapPage() {
  const [params] = useSearchParams();
  const focusId = params.get('focus');
  const navigate = useNavigate();
  const [view, setView] = useState('Gantt');
  const [scale, setScale] = useState('M');
  const [selectedDependency, setSelectedDependency] = useState(null);
  const projects = useApi('/projects', { query: { limit: 200 } });
  const milestones = useApi('/milestones', { query: { limit: 200 } });
  const workItems = useApi('/work-items', { query: { limit: 400 } });
  const roadmapItems = useApi('/roadmap/items', { query: { limit: 200 } });
  const roadmapDependencies = useApi('/roadmap/dependencies', {
    query: { limit: 200, projectId: focusId || undefined },
  });
  const usersRes = useApi('/users', { query: { limit: 200 } });
  const deliverySummary = useApi(focusId ? `/projects/${focusId}/delivery-summary` : null, {
    skip: !focusId,
  });
  const inspector = useInspector();
  const update = useMutation();
  const createEpic = useMutation();
  const { user } = useCurrentUser();
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [epicDrawerOpen, setEpicDrawerOpen] = useState(false);
  const [cascadeReschedule, setCascadeReschedule] = useState(true);
  const [rescheduleOutcome, setRescheduleOutcome] = useState(null);

  const projectsWithSummary = useMemo(() => {
    const summary = deliverySummary.data?.data;
    return (projects.data?.data || []).map((project) =>
      project.id === focusId
        ? {
            ...project,
            criticalPath: Boolean(summary?.criticalPathItemCount),
          }
        : project,
    );
  }, [deliverySummary.data, focusId, projects.data]);

  const workItemList = useMemo(() => workItems.data?.data || [], [workItems.data]);
  const epicList = useMemo(
    () =>
      workItemList.filter((item) =>
        ['epic', 'feature-epic'].includes(String(item.itemType || item.type || '').toLowerCase()),
      ),
    [workItemList],
  );
  const focusedDependencyList = useMemo(
    () => roadmapDependencies.data?.data || [],
    [roadmapDependencies.data],
  );
  const roadmapTaskBars = useMemo(
    () =>
      deriveRoadmapTaskBars({
        projects: projectsWithSummary,
        workItems: workItemList,
        roadmapItems: roadmapItems.data?.data || [],
        dependencies: focusedDependencyList,
        focusId,
      }),
    [focusId, focusedDependencyList, projectsWithSummary, roadmapItems.data, workItemList],
  );
  const focusedProject = useMemo(
    () => projectsWithSummary.find((project) => project.id === focusId) || null,
    [focusId, projectsWithSummary],
  );
  const selectedRoadmapItem = useMemo(() => {
    if (!inspector.target?.id) return null;
    return (
      (roadmapItems.data?.data || []).find(
        (item) => item.scopeType === 'project' && item.scopeId === inspector.target.id,
      ) || null
    );
  }, [inspector.target?.id, roadmapItems.data]);
  const focusMetrics = useMemo(
    () => buildFocusMetrics(focusedProject, deliverySummary.data?.data || null),
    [deliverySummary.data, focusedProject],
  );
  const focusDeliverySummary = deliverySummary.data?.data || null;
  const sprintSummary = focusDeliverySummary?.sprintSummary || null;
  const statusBuckets = useMemo(
    () =>
      Object.entries(focusDeliverySummary?.statusBuckets || {}).filter(
        ([, count]) => Number(count) > 0,
      ),
    [focusDeliverySummary],
  );
  const lanes = useMemo(
    () =>
      buildLanes(
        projectsWithSummary,
        epicList,
        milestones.data?.data || [],
        focusId,
        roadmapTaskBars,
      ),
    [projectsWithSummary, epicList, milestones.data, focusId, roadmapTaskBars],
  );

  const aggregateKpis = useMemo(
    () =>
      buildAggregateKpis({
        projects: projectsWithSummary,
        projectTotal: projects.data?.page?.total,
        milestones: milestones.data?.data || [],
        workItems: workItemList,
        lanes,
      }),
    [projectsWithSummary, projects.data?.page?.total, milestones.data, workItemList, lanes],
  );

  const inspectorHealthSeries = useMemo(
    () => buildInspectorHealthSeries(inspector.target),
    [inspector.target],
  );

  // AC-CMD-3.1 — the estimated legend renders only when at least one bar's
  // geometry was synthesized rather than authored.
  const hasEstimatedBars = useMemo(
    () => lanes.some((lane) => lane.items.some((item) => item.datesEstimated)),
    [lanes],
  );

  // TT-07a — an inspected item without authored schedule dates gets an honest
  // "not scheduled" note plus the existing Edit path to author them.
  const targetDatesMissing = useMemo(() => {
    const t = inspector.target;
    if (!t) return false;
    const hasStart = Boolean(t.startDate || t.plannedStartDate || t.lifecycleDates?.start);
    const hasEnd = Boolean(t.endDate || t.plannedEndDate || t.lifecycleDates?.end);
    return !hasStart || !hasEnd;
  }, [inspector.target]);

  useEffect(() => {
    if (!focusId) return;
    const target =
      (projects.data?.data || []).find((p) => p.id === focusId) ||
      epicList.find((e) => e.id === focusId);
    if (target) inspector.open(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, projects.data?.data, epicList]);

  useEffect(() => {
    setSelectedDependency(null);
  }, [focusId]);

  // Keep edit state in sync with the currently-selected target.
  useEffect(() => {
    setEdit(projectToEditState(inspector.target));
    setEditing(false);
    setCascadeReschedule(true);
  }, [inspector.target]);

  const editingProject = inspector.target && (inspector.target.itemType ? false : true); // crude: epics have itemType
  const inspectorEntityType =
    inspector.target?.itemType || inspector.target?.type ? 'work_item' : 'project';

  async function handleSaveInspector() {
    const target = inspector.target;
    if (!target) return;
    try {
      const isWorkItem = inspectorEntityType === 'work_item';
      const path = isWorkItem ? `/work-items/${target.id}` : `/projects/${target.id}`;
      const scheduleChanged =
        !isWorkItem &&
        selectedRoadmapItem &&
        ((edit.startDate || null) !== (target.startDate || target.lifecycleDates?.start || null) ||
          (edit.endDate || null) !== (target.endDate || target.lifecycleDates?.end || null));
      let cascadedWorkItems = [];

      if (scheduleChanged) {
        const scheduleResponse = await update.run(() =>
          api.post('/roadmap/reschedule', {
            roadmapItemId: selectedRoadmapItem.id,
            startDate: edit.startDate || null,
            endDate: edit.endDate || null,
            cascade: cascadeReschedule,
          }),
        );
        const scheduleData = scheduleResponse?.data || scheduleResponse;
        const rescheduledRoadmapItem = scheduleData?.roadmapItem;
        cascadedWorkItems = scheduleData?.cascadedWorkItems || [];
        roadmapItems.setData?.((prev) => updateEnvelopeRow(prev, rescheduledRoadmapItem));
        workItems.setData?.((prev) => mergeEnvelopeRows(prev, cascadedWorkItems));
        setRescheduleOutcome({
          roadmapItemId: rescheduledRoadmapItem?.id || selectedRoadmapItem.id,
          cascadedWorkItems,
        });
      } else {
        setRescheduleOutcome(null);
      }

      const patchBody = isWorkItem
        ? {
            title: edit.name,
            status: edit.status,
            ownerUserId: edit.ownerUserId || null,
            startDate: edit.startDate || null,
            endDate: edit.endDate || null,
          }
        : {
            name: edit.name,
            status: edit.status,
            projectManagerUserId: edit.ownerUserId || null,
            startDate: edit.startDate || null,
            endDate: edit.endDate || null,
            healthScores: {
              ...(target.healthScores || {}),
              schedule: edit.schedule === '' ? undefined : Number(edit.schedule),
              cost: edit.cost === '' ? undefined : Number(edit.cost),
              risk: edit.risk === '' ? undefined : Number(edit.risk),
              missionImpact: edit.missionImpact === '' ? undefined : Number(edit.missionImpact),
            },
            budgetUsdM: edit.budgetUsdM === '' ? undefined : Number(edit.budgetUsdM),
          };
      const res = await update.run(() => api.patch(path, patchBody));
      const updated = res.data || res;
      // Update local data so Gantt redraws.
      if (isWorkItem) {
        workItems.setData?.((prev) => updateEnvelopeRow(prev, updated));
      } else {
        projects.setData?.((prev) => updateEnvelopeRow(prev, updated));
      }
      inspector.open(updated);
      setEditing(false);
      toast.success({
        title: scheduleChanged ? 'Roadmap rescheduled' : 'Saved',
        body: scheduleChanged
          ? `${updated.name || updated.title} updated. ${cascadedWorkItems.length} linked work item${cascadedWorkItems.length === 1 ? '' : 's'} cascaded.`
          : `${updated.name || updated.title} updated.`,
      });
      activityRail.push({
        action: scheduleChanged
          ? 'roadmap_item.reschedule'
          : isWorkItem
            ? 'work_item.update'
            : 'project.update',
        targetType: scheduleChanged ? 'roadmap_item' : isWorkItem ? 'work_item' : 'project',
        targetId: target.id,
        actorUserId: user?.id || 'unknown',
        summary: scheduleChanged
          ? `${updated.name || updated.title} rescheduled with ${cascadedWorkItems.length} cascaded work item${cascadedWorkItems.length === 1 ? '' : 's'}.`
          : `${updated.name || updated.title} · ${edit.status || target.status || '—'}`,
      });
    } catch (e) {
      toast.error({ title: 'Could not save', body: e.message });
    }
  }

  async function handleCreateEpic(payload) {
    try {
      const res = await createEpic.run(() => api.post('/work-items', payload));
      const created = res.data || res;
      workItems.setData?.((prev) => {
        const current = prev?.data?.data || [];
        return prev
          ? { ...prev, data: { ...(prev.data || {}), data: [created, ...current] } }
          : prev;
      });
      setEpicDrawerOpen(false);
      toast.success({
        title: 'Epic created',
        body: `${created.id} · ${created.title}`,
      });
      activityRail.push({
        action: 'work_item.create',
        targetType: 'work_item',
        targetId: created.id,
        actorUserId: user?.id || 'unknown',
        summary: `Created epic ${created.title?.slice(0, 64)} on ${created.projectId}`,
      });
      inspector.open(created);
    } catch (e) {
      toast.error({ title: 'Could not create epic', body: e.message });
    }
  }

  if (
    projects.loading ||
    milestones.loading ||
    workItems.loading ||
    roadmapItems.loading ||
    roadmapDependencies.loading
  ) {
    return (
      <div className="roadmap-loading">
        <KpiBandSkeleton count={4} />
        <div className="card flush roadmap-panel-state">
          <TableSkeleton rows={8} />
        </div>
      </div>
    );
  }
  if (projects.error) return <ErrorState error={projects.error} />;
  if (!lanes.length) {
    return (
      <div className="card flush roadmap-panel-state">
        <BrandedEmptyState
          icon="🗺️"
          eyebrow="Strategic roadmap"
          title="No initiatives on the roadmap yet"
          description="Once portfolios, projects, and epics carry schedule dates, they appear here as a sequenced delivery timeline."
          action={
            <button className="btn primary tactile-press" onClick={() => setEpicDrawerOpen(true)}>
              New epic
            </button>
          }
          tone="blue"
        />
        <NewEpicDrawer
          open={epicDrawerOpen}
          onClose={() => setEpicDrawerOpen(false)}
          onSubmit={handleCreateEpic}
          submitting={createEpic.running}
          defaults={{ projectId: focusId || '', ownerUserId: user?.id }}
        />
      </div>
    );
  }

  const totalItems = lanes.reduce((a, l) => a + l.items.length, 0);
  const users = usersRes.data?.data || [];

  return (
    <>
      {/* PRIME-SHELL-UX-001: single body page identity + relocated About. */}
      <PageHeader />
      <PageActionStrip
        filters={
          <>
            <div className="seg" role="tablist" aria-label="Time scale">
              {SCALES.map((s) => (
                <button
                  key={s}
                  role="tab"
                  aria-selected={scale === s}
                  className={`seg-btn ${scale === s ? 'active' : ''}`}
                  onClick={() => setScale(s)}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="seg" role="tablist" aria-label="View mode">
              {VIEWS.map((v) => (
                <button
                  key={v}
                  role="tab"
                  aria-selected={view === v}
                  className={`seg-btn ${view === v ? 'active' : ''}`}
                  onClick={() => setView(v)}
                >
                  {v}
                </button>
              ))}
            </div>
          </>
        }
        actions={
          <button className="btn primary tactile-press" onClick={() => setEpicDrawerOpen(true)}>
            New epic
          </button>
        }
      />

      {!focusId ? (
        <KpiHeroBand
          className="roadmap-hero-band"
          eyebrow="Roadmap at a glance"
          asOf={projects.loadedAt}
          items={aggregateKpis}
          columns={4}
        />
      ) : null}

      {focusMetrics.length ? (
        <section className="bento roadmap-metrics">
          {focusMetrics.map((metric) => (
            <article
              key={metric.label}
              className={`span-3 card roadmap-metric tone-${metric.tone}`}
            >
              <div className="eyebrow">{metric.label}</div>
              <strong>{metric.value}</strong>
              <p>{metric.detail}</p>
            </article>
          ))}
          {sprintSummary ? (
            <article className="span-3 card roadmap-metric tone-blue">
              <div className="eyebrow">Sprint delivery</div>
              <strong>
                {sprintSummary.completedPoints || 0} / {sprintSummary.committedPoints || 0} points
              </strong>
              <p>
                {sprintSummary.activeSprintCount || 0} active sprint window
                {sprintSummary.activeSprintCount === 1 ? '' : 's'} in the focused delivery plan.
              </p>
            </article>
          ) : null}
          {statusBuckets.length ? (
            <article className="span-3 card roadmap-metric tone-slate">
              <div className="eyebrow">Status buckets</div>
              <div
                className="row"
                style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}
              >
                {statusBuckets.map(([status, count]) => (
                  <span key={status} className="chip outline sm">
                    {formatStatusBucketLabel(status)} {count}
                  </span>
                ))}
              </div>
              <p>Focused roadmap work is grouped into visible execution-status buckets.</p>
            </article>
          ) : null}
        </section>
      ) : null}
      {rescheduleOutcome ? (
        <section
          className="card roadmap-reschedule-summary"
          data-testid="roadmap-reschedule-summary"
        >
          <div className="row spread" style={{ alignItems: 'center', gap: 12 }}>
            <div>
              <div className="eyebrow">Latest reschedule</div>
              <strong>
                {rescheduleOutcome.cascadedWorkItems.length} linked work item
                {rescheduleOutcome.cascadedWorkItems.length === 1 ? '' : 's'} cascaded
              </strong>
              <p className="muted small">
                {rescheduleOutcome.cascadedWorkItems.length
                  ? rescheduleOutcome.cascadedWorkItems.map((item) => item.id).join(', ')
                  : 'No downstream items needed to move.'}
              </p>
            </div>
            <StatusChip
              status={
                rescheduleOutcome.cascadedWorkItems.length ? 'cascade applied' : 'dates updated'
              }
            />
          </div>
        </section>
      ) : null}

      <section className="bento roadmap-bento">
        <div className={`${inspector.isOpen ? 'span-8' : 'span-12'} card flush roadmap-panel`}>
          <header
            className="row spread"
            style={{ padding: 'var(--space-4) var(--space-5)', borderBottom: 'var(--hairline)' }}
          >
            <div>
              <div className="eyebrow">
                {totalItems} initiatives · {lanes.length} portfolios
              </div>
              <h2 className="card-title">{view} workspace</h2>
            </div>
          </header>
          {view === 'Gantt' && hasEstimatedBars ? (
            <div className="roadmap-gantt-legend" data-testid="roadmap-estimated-legend">
              <span className="roadmap-gantt-legend__entry">
                <span className="roadmap-gantt-legend__swatch is-authored" aria-hidden />
                Authored dates
              </span>
              <span className="roadmap-gantt-legend__entry">
                <span className="roadmap-gantt-legend__swatch is-estimated" aria-hidden />
                Estimated — dates not set
              </span>
            </div>
          ) : null}
          {view === 'Gantt' ? (
            <GanttPanel
              lanes={lanes}
              dependencies={focusedDependencyList}
              onSelect={(item) => inspector.open(item.__raw || item)}
              selectedId={inspector.target?.id}
              onSelectDependency={setSelectedDependency}
              selectedDependencyId={selectedDependency?.id}
              height={760}
            />
          ) : view === 'List' ? (
            <ListView lanes={lanes} onSelect={(it) => inspector.open(it.__raw)} />
          ) : (
            <BoardView lanes={lanes} onSelect={(it) => inspector.open(it.__raw)} />
          )}
          {selectedDependency ? (
            <div className="roadmap-dependency-banner" data-testid="roadmap-dependency-banner">
              <div>
                <div className="eyebrow">Selected dependency</div>
                <strong>
                  {selectedDependency.predecessorTitle || selectedDependency.predecessorId}
                  {' -> '}
                  {selectedDependency.successorTitle || selectedDependency.successorId}
                </strong>
              </div>
              <StatusChip
                status={selectedDependency.criticalPathFlag ? 'critical path' : 'active'}
              />
            </div>
          ) : null}
        </div>
        {inspector.isOpen ? (
          <div className="span-4">
            <InspectorCard
              embedded
              eyebrow={inspectorEntityType === 'work_item' ? 'Epic' : 'Initiative'}
              title={inspector.target?.name || inspector.target?.title || '—'}
              onClose={inspector.close}
              actions={
                editing ? (
                  <>
                    <button
                      className="btn ghost sm"
                      onClick={() => setEditing(false)}
                      disabled={update.running}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn primary sm tactile-press"
                      data-testid="inspector-save"
                      onClick={handleSaveInspector}
                      disabled={update.running}
                    >
                      {update.running ? 'Saving…' : 'Save changes'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn ghost sm"
                      onClick={() =>
                        navigate(`/projects/${inspector.target?.projectId || inspector.target?.id}`)
                      }
                    >
                      Open project →
                    </button>
                    <button
                      className="btn primary sm tactile-press"
                      data-testid="inspector-edit"
                      onClick={() => setEditing(true)}
                    >
                      Edit
                    </button>
                  </>
                )
              }
            >
              {editing ? (
                <>
                  <FormField label="Name" htmlFor="ri-name" required>
                    <TextInput
                      id="ri-name"
                      value={edit.name}
                      onChange={(v) => setEdit((s) => ({ ...s, name: v }))}
                    />
                  </FormField>
                  <FormField label="Status" htmlFor="ri-status">
                    <Select
                      id="ri-status"
                      value={edit.status}
                      onChange={(v) => setEdit((s) => ({ ...s, status: v }))}
                      options={STATUS_OPTIONS}
                    />
                  </FormField>
                  <FormField label="Owner" htmlFor="ri-owner">
                    <Select
                      id="ri-owner"
                      value={edit.ownerUserId}
                      onChange={(v) => setEdit((s) => ({ ...s, ownerUserId: v }))}
                      options={[
                        { value: '', label: 'Unassigned' },
                        ...users.slice(0, 50).map((u) => ({
                          value: u.id,
                          label: `${u.displayName || u.id} (${u.id})`,
                        })),
                      ]}
                    />
                  </FormField>
                  <div className="form-row-pair">
                    <FormField label="Start date" htmlFor="ri-start">
                      <DateInput
                        id="ri-start"
                        value={edit.startDate}
                        onChange={(v) => setEdit((s) => ({ ...s, startDate: v }))}
                      />
                    </FormField>
                    <FormField label="End date" htmlFor="ri-end">
                      <DateInput
                        id="ri-end"
                        value={edit.endDate}
                        onChange={(v) => setEdit((s) => ({ ...s, endDate: v }))}
                      />
                    </FormField>
                  </div>
                  {editingProject && selectedRoadmapItem ? (
                    <FormField
                      label="Reschedule options"
                      htmlFor="ri-cascade"
                      helperText={`Applies to ${selectedRoadmapItem.id} and can cascade linked work items.`}
                    >
                      <label className="rapid-checkbox" htmlFor="ri-cascade">
                        <input
                          id="ri-cascade"
                          type="checkbox"
                          checked={cascadeReschedule}
                          onChange={(event) => setCascadeReschedule(event.target.checked)}
                        />
                        Cascade linked work items when dates move
                      </label>
                    </FormField>
                  ) : null}
                  {editingProject ? (
                    <>
                      <div className="form-row-pair">
                        <FormField label="Schedule" htmlFor="ri-sched">
                          <NumberInput
                            id="ri-sched"
                            value={edit.schedule}
                            onChange={(v) => setEdit((s) => ({ ...s, schedule: v }))}
                            min={0}
                            max={100}
                          />
                        </FormField>
                        <FormField label="Cost" htmlFor="ri-cost">
                          <NumberInput
                            id="ri-cost"
                            value={edit.cost}
                            onChange={(v) => setEdit((s) => ({ ...s, cost: v }))}
                            min={0}
                            max={100}
                          />
                        </FormField>
                      </div>
                      <div className="form-row-pair">
                        <FormField label="Risk" htmlFor="ri-risk">
                          <NumberInput
                            id="ri-risk"
                            value={edit.risk}
                            onChange={(v) => setEdit((s) => ({ ...s, risk: v }))}
                            min={0}
                            max={100}
                          />
                        </FormField>
                        <FormField label="Mission impact" htmlFor="ri-mission">
                          <NumberInput
                            id="ri-mission"
                            value={edit.missionImpact}
                            onChange={(v) => setEdit((s) => ({ ...s, missionImpact: v }))}
                            min={0}
                            max={100}
                          />
                        </FormField>
                      </div>
                      <FormField label="Budget" htmlFor="ri-budget">
                        <NumberInput
                          id="ri-budget"
                          value={edit.budgetUsdM}
                          onChange={(v) => setEdit((s) => ({ ...s, budgetUsdM: v }))}
                          suffix="$M"
                        />
                      </FormField>
                    </>
                  ) : null}
                  <FormField label="Notes" htmlFor="ri-notes">
                    <Textarea
                      id="ri-notes"
                      value={edit.notes}
                      onChange={(v) => setEdit((s) => ({ ...s, notes: v }))}
                      rows={2}
                      placeholder="Optional — recorded in the audit payload."
                    />
                  </FormField>
                </>
              ) : (
                <>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="chip outline sm">{inspector.target?.id}</span>
                    <StatusChip status={inspector.target?.status} />
                    <HealthIndicator
                      health={inspector.target?.healthScores?.overall || inspector.target?.status}
                    />
                  </div>
                  {inspectorHealthSeries ? (
                    <div className="roadmap-inspector-trend">
                      <div className="roadmap-inspector-trend__caption">
                        <span className="eyebrow">Health profile</span>
                        <span className="mono">
                          {inspectorHealthSeries.min}–{inspectorHealthSeries.max}
                        </span>
                      </div>
                      <Sparkline
                        values={inspectorHealthSeries.values}
                        width={260}
                        height={42}
                        tone={bandTone(inspector.target?.healthScores?.overall, {
                          good: 80,
                          warn: 50,
                        })}
                        ariaLabel={`Health profile across ${inspectorHealthSeries.labels.join(', ')}`}
                      />
                      <div className="roadmap-inspector-trend__caption">
                        <span className="muted small">
                          {inspectorHealthSeries.labels.join(' · ')}
                        </span>
                      </div>
                    </div>
                  ) : null}
                  <p>
                    {inspector.target?.description?.slice(0, 280) ||
                      inspector.target?.summary?.slice(0, 280) ||
                      '—'}
                  </p>
                  <dl className="inspector-dl">
                    <div>
                      <dt>Owner</dt>
                      <dd>
                        {inspector.target?.projectManagerUserId ||
                          inspector.target?.ownerUserId ||
                          '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>Stage</dt>
                      <dd>{inspector.target?.lifecycleStage || '—'}</dd>
                    </div>
                    <div>
                      <dt>Schedule</dt>
                      <dd className="mono">{inspector.target?.healthScores?.schedule ?? '—'}</dd>
                    </div>
                    <div>
                      <dt>Cost</dt>
                      <dd className="mono">{inspector.target?.healthScores?.cost ?? '—'}</dd>
                    </div>
                    <div>
                      <dt>Risk</dt>
                      <dd className="mono">{inspector.target?.healthScores?.risk ?? '—'}</dd>
                    </div>
                    <div>
                      <dt>Mission</dt>
                      <dd className="mono">
                        {inspector.target?.healthScores?.missionImpact ?? '—'}
                      </dd>
                    </div>
                  </dl>
                  {targetDatesMissing ? (
                    <p className="roadmap-dates-note" data-testid="roadmap-dates-note">
                      Dates not set — edit item to schedule. Its bar renders as an estimated
                      placeholder until dates are authored.
                    </p>
                  ) : null}
                  <div className="eyebrow">Source</div>
                  {/* TT-07c — no invented "PMRT · current" provenance; absent
                      dataTrust renders as honest em dashes. */}
                  <p className="mono small" data-testid="roadmap-inspector-source">
                    {inspector.target?.dataTrust?.sourceSystem || '—'} · freshness{' '}
                    {inspector.target?.dataTrust?.freshness || '—'} · confidence{' '}
                    {inspector.target?.dataTrust?.confidence ?? '—'}
                  </p>
                </>
              )}
            </InspectorCard>
          </div>
        ) : null}
      </section>

      <NewEpicDrawer
        open={epicDrawerOpen}
        onClose={() => setEpicDrawerOpen(false)}
        onSubmit={handleCreateEpic}
        submitting={createEpic.running}
        defaults={{ projectId: focusId || '', ownerUserId: user?.id }}
      />
    </>
  );
}

function ListView({ lanes, onSelect }) {
  const rows = lanes.flatMap((lane) => lane.items.map((it) => ({ lane, item: it })));
  const pager = useClientPagination(rows, {
    initialPageSize: 10,
    resetKey: `roadmap-list-${rows.length}`,
  });
  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>Initiative</th>
            <th>Portfolio</th>
            <th>Status</th>
            <th>Start</th>
            <th>End</th>
            <th>Health</th>
            <th>Progress</th>
          </tr>
        </thead>
        <tbody>
          {pager.items.map(({ lane, item: it }) => {
            const overall = it.__raw?.healthScores?.overall;
            const hasHealth = overall != null && !Number.isNaN(Number(overall));
            return (
              <tr
                key={`${lane.id}-${it.id}`}
                className="row-hover"
                onClick={() => onSelect && onSelect(it)}
              >
                <td>
                  <strong>{it.name}</strong>
                  <div className="muted small">{it.id}</div>
                </td>
                <td className="mono">{lane.id}</td>
                <td>
                  <StatusChip status={it.__raw?.status} />
                </td>
                <td>{it.startDate}</td>
                <td>{it.endDate}</td>
                <td className="roadmap-list-score">
                  {hasHealth ? (
                    <InlineScoreCell
                      value={Number(overall)}
                      max={100}
                      thresholds={{ good: 80, warn: 50 }}
                    />
                  ) : (
                    <span className="muted small">—</span>
                  )}
                </td>
                <td>
                  {/* TT-07b — progress renders only when reported; otherwise an
                      honest label instead of an empty/fake bar. */}
                  {it.progress != null ? (
                    <div className="score-bar" style={{ width: 120 }}>
                      <i
                        className={bandTone(Math.round(it.progress * 100), {
                          good: 80,
                          warn: 50,
                        })}
                        style={{ width: `${Math.round(it.progress * 100)}%` }}
                      />
                    </div>
                  ) : (
                    <span className="muted small">Not reported</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <PageViewerControls
        page={pager.page}
        label="initiatives"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </>
  );
}

function BoardView({ lanes, onSelect }) {
  const pager = useClientPagination(lanes, {
    initialPageSize: 5,
    resetKey: `roadmap-board-${lanes.length}`,
  });
  return (
    <>
      <div className="bento" style={{ padding: 'var(--space-4)' }}>
        {pager.items.map((lane) => (
          <div key={lane.id} className="span-6 card">
            <h3 className="card-title">{lane.label}</h3>
            <RoadmapBoardLane lane={lane} onSelect={onSelect} />
          </div>
        ))}
      </div>
      <PageViewerControls
        page={pager.page}
        label="portfolio lanes"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </>
  );
}

function RoadmapBoardLane({ lane, onSelect }) {
  const pager = useClientPagination(lane.items, {
    initialPageSize: 8,
    resetKey: lane.id,
  });
  return (
    <>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {pager.items.map((it) => (
          <li
            key={it.id}
            className="row spread"
            style={{ padding: '8px 0', borderBottom: 'var(--hairline)', cursor: 'pointer' }}
            onClick={() => onSelect && onSelect(it)}
          >
            <span>{it.name}</span>
            <StatusChip status={it.__raw?.status} />
          </li>
        ))}
      </ul>
      <PageViewerControls
        page={pager.page}
        label={`${lane.label} initiatives`}
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </>
  );
}
